import { Col, Divider, Row } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BorderOutlined, CheckSquareOutlined } from '@ant-design/icons';
import { PaymentButton } from '@common/components/PaymentButton/PaymentButton';
import { DEFAULT_SAVE_ACTION, EE_STATUS, SaveActionType } from '@common/constants';
import { getBestPaymentUrl, isReadyForPayment, notifyFromStatus } from '@common/helpers';
import { useQueryStringParameters } from '@common/hooks/useQueryStringParameters/useQueryStringParameters';
import { CustomerInfo, PricedProduct, SaveAction, StoreModel } from '@common/models';
import { GetEstimateDto } from '@common/models/save-estimate-dto';
import PrintEstimateButton from '@components/buttons/PrintEstimateButton/PrintEstimateButton';
import SaveButton from '@components/buttons/save-estimate-button/save-button';
import ConfirmSmallProject from '@core/components/ConfirmSmallProject/ConfirmSmallProject';
import CustomerDataModal from '@core/components/CustomerDataModal/CustomerDataModal';
import { CustomerExpectationModal } from '@core/components/CustomerExpectationsModal/CustomerExpecationModal';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import useEstimateSaver from '@core/hooks/useEstimateSaver';
import useEstimateStatus from '@core/hooks/useEstimateStatus';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import useResetableState from '@core/hooks/useResetableState';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSmallJobs } from '@core/hooks/useSmallJobs';
import { PDFType, downloadPdf } from '@ee-api/estimate-pdf-api/estimatePdfService';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { usePricedProductsContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    selectCustomerData,
    selectIsFetchedCustomerData,
    selectIsSavingEstimate,
    selectValidSmallTicketNumber,
    selecteEeStatusList,
    updateCustomerData,
    updateIsSavingEstimate,
    updateValidSmallTicketNumber,
} from '@redux/slices/estimateSlice';

import { PasswordModal } from './components/PasswordModal';
import { ProductSummary } from './components/ProductSummary';
import { getPromotionEndDate } from './estimate-section.utils';
import { PricingRow } from './line-item-helper';

import './EstimateSection.scss';

export interface EstimateSectionProps {
    agreementSelected: boolean;
    setAgreementSelected: React.Dispatch<React.SetStateAction<boolean>>;
    showCustomerExpecationModal: boolean;
    setShowCustomerExpecationModal: React.Dispatch<React.SetStateAction<boolean>>;
    onClose: () => void;
}

enum SAVE_PROCESS {
    NOT_STARTED,
    CONFIRM_SMALL_PROJECT,
    GET_CUSTOMER_DATA,
    READY_TO_SAVE,
}

const emptyArray = [] satisfies SaveAction[];

const printPdf = async ({
    fetchedEstimate,
    pdfType,
    userToken,
    password,
}: {
    fetchedEstimate: GetEstimateDto | undefined;
    pdfType: PDFType;
    userToken?: string;
    password?: string;
}) => {
    if (!fetchedEstimate?.code) {
        return;
    }
    try {
        await downloadPdf({
            code: fetchedEstimate?.code,
            filename: `${fetchedEstimate?.estimateNumber}${pdfType === PDFType.INTERNAL ? '-internal' : ''}.pdf`,
            pdfType,
            userToken,
            password,
        });
        return true;
    } catch (error) {
        console.warn(error);
        return false;
    }
};

const EstimateSection = ({
    agreementSelected,
    setAgreementSelected,
    setShowCustomerExpecationModal,
    showCustomerExpecationModal,
    onClose,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
EstimateSectionProps): JSX.Element => {
    const { t } = useTranslation();
    const { selectedValue: selectedStore } = useSelectedStore();

    const [saveProcessState, setSaveProcessState] = useState(SAVE_PROCESS.NOT_STARTED);
    const [saveAction, setSaveAction] = useState<SaveActionType>(DEFAULT_SAVE_ACTION);

    const { userOptions, retailerSettings, retailerFileSettings } = useRetailerSettings();

    const {
        pricedProducts,
        matchingMileageTierAsPricedProduct,
        priceTotals,
        priceTotalsWithoutDelivery,
        pricedProductsWithoutDelivery,
    } = usePricedProductsContext();

    const { useMinQtyAreaForPricing, smallJobMinimumArea, canOverrideSmallJob } = useSmallJobs();

    const { saveEstimate, isEstimateDirty, getDisplayDates, isSaveLoading } = useEstimateSaver();

    const { fetchedEstimate } = useFetchedEstimate();
    const { estimateStatus } = useEstimateStatus();
    const eeStatusList = useAppSelector(selecteEeStatusList);
    const isSavingEstimate = useAppSelector(selectIsSavingEstimate);
    const customerData = useAppSelector(selectCustomerData);
    const isFetchedCustomerData = useAppSelector(selectIsFetchedCustomerData);

    const { selectedValue: selectedMaterial } = useSelectedMaterial();

    const promotionEndDate = getPromotionEndDate(pricedProducts);
    const { quoteDate: displayedQuoteDate, expireDate: displayedExpireDate } = getDisplayDates(
        promotionEndDate ? new Date(promotionEndDate) : undefined
    );
    const dispatch = useAppDispatch();

    const { userToken } = useQueryStringParameters();
    const [printPassword, setPrintPassword, resetPrintPassword] = useResetableState('');

    const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);

    const [isMounted, setIsMounted] = useState(false);

    const validatedTicketNumber = useAppSelector(selectValidSmallTicketNumber);

    const { trackCustomerInfoSave, trackBeginCheckout } = useAnalyticsEvents();

    const paymentUrl = getBestPaymentUrl(estimateStatus, fetchedEstimate);
    const isPayable = paymentUrl && isReadyForPayment(estimateStatus, fetchedEstimate) && !isEstimateDirty;

    const isSavable = !eeStatusList.includes(EE_STATUS.NO_SAVE);
    const isSaveButtonEnabled = isEstimateDirty && agreementSelected && isSavable;
    const isPrintingEnabled = !!fetchedEstimate?.code && !isEstimateDirty;
    const showSaveButtons = isSavable && !isPrintingEnabled && !isPayable;

    const isSmallProjectModalVisible = saveProcessState === SAVE_PROCESS.CONFIRM_SMALL_PROJECT;
    const isSaveModalVisible = saveProcessState === SAVE_PROCESS.GET_CUSTOMER_DATA;

    const handleBoarderedOutlineClick = () => {
        setAgreementSelected(true);
    };
    const handleCheckOutlinedClick = () => {
        setAgreementSelected(false);
    };
    const handleCustomerExpectationsClick = () => {
        setShowCustomerExpecationModal(true);
    };

    const ProductList = ({ pricedProducts }: { pricedProducts: PricedProduct[] }): JSX.Element => {
        return (
            <div id="Line Items" className="line-items">
                {pricedProducts.map((pricedItem: PricedProduct, index: number) => (
                    <PricingRow
                        key={index}
                        item={pricedItem}
                        parity={index % 2}
                        hasMinimumQuantityWarning={useMinQtyAreaForPricing}
                    />
                ))}
            </div>
        );
    };

    const CustomerAgreement = () => {
        return (
            <Row id="customer-agreement" className="customer-agreement">
                <div className="check-box">
                    <BorderOutlined
                        className={agreementSelected ? 'empty-check-box-disabled' : 'empty-check-box-enabled'}
                        onClick={handleBoarderedOutlineClick}
                    />
                    <CheckSquareOutlined
                        className={!agreementSelected ? 'full-check-box-disabled' : 'full-check-box-enabled'}
                        onClick={handleCheckOutlinedClick}
                    />
                </div>
                <div className="agreement-text">{t('ORDER_SUMMARY.ESTIMATE_SECTION.AGREEMENT_TEXT')}</div>
                <div className="agreement-modal-text agreement-text" onClick={handleCustomerExpectationsClick}>
                    {t('ORDER_SUMMARY.ESTIMATE_SECTION.AGREEMENT_TEXT_CLICKABLE')}
                </div>
            </Row>
        );
    };

    const EstimateInformation = ({ quoteDate, quoteExpiration }: { quoteDate: Date; quoteExpiration: Date }) => {
        return (
            <div className="estimator-information">
                <div className="estimator-info-title">
                    {t('ORDER_SUMMARY.ESTIMATE_SECTION.ESTIMATOR_INFORMATION_TITLE')}
                </div>
                <Row>
                    <Col span={8}>
                        <div className="quote-text">{t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.QUOTE')}</div>
                    </Col>
                    <Col span={8}>
                        <div className="current-date">{quoteDate.toLocaleDateString()}</div>
                    </Col>
                </Row>
                <Row>
                    <Col span={8}>
                        <div className="quote-text">{t('ORDER_SUMMARY.ESTIMATE_SECTION.ORDER.EXPIRE')}</div>
                    </Col>
                    <Col span={8}>
                        <div className="current-date">{quoteExpiration.toLocaleDateString()}</div>
                    </Col>
                </Row>
            </div>
        );
    };

    const StoreInformation = ({ store }: { store?: StoreModel }): JSX.Element => {
        return (
            <div className="store-info">
                <div className="store-info-title">{t('ORDER_SUMMARY.ESTIMATE_SECTION.STORE_INFORMATION_TITLE')}</div>
                <div className="store-info-name">{store?.companyName}</div>
                <div className="store-info-name">{store?.address}</div>
            </div>
        );
    };

    const AdditionalInformation = () => {
        return (
            <div className="additional-information">
                <Row>
                    <Col span={12}>
                        <EstimateInformation quoteDate={displayedQuoteDate} quoteExpiration={displayedExpireDate} />
                    </Col>
                    <Col span={12}>
                        <StoreInformation store={selectedStore} />
                    </Col>
                </Row>
            </div>
        );
    };

    const onCustomerDataSave = (customerInfo: CustomerInfo) => {
        dispatch(updateCustomerData(customerInfo));
        trackCustomerInfoSave({ pricedProducts, priceTotals });
        saveEstimate(customerInfo, saveAction);
    };

    // clean up after the save process is complete
    useEffect(() => {
        if (!isMounted || isSaveLoading) {
            return;
        }
        setSaveProcessState(SAVE_PROCESS.NOT_STARTED);
        // onClose(); // not sure why we return to the main page, either we have an error or we're reloading a new estimate
    }, [isMounted, isSaveLoading]);

    const onCustomerDataCancel = () => {
        setSaveProcessState(SAVE_PROCESS.NOT_STARTED);
    };

    const onSmallProjectSuccess = (ticketNumber: string) => {
        dispatch(updateValidSmallTicketNumber(ticketNumber));
        setSaveProcessState(SAVE_PROCESS.GET_CUSTOMER_DATA);
    };

    const onSmallProjectContinue = () => {
        dispatch(updateValidSmallTicketNumber(undefined)); // make certain there's no ticket number
        setSaveProcessState(SAVE_PROCESS.GET_CUSTOMER_DATA);
    };

    const onSmallProjectCancel = () => {
        dispatch(updateValidSmallTicketNumber(undefined)); // make certain there's no ticket number
        setSaveProcessState(SAVE_PROCESS.NOT_STARTED);
    };

    // This useEffect must be the last one
    useEffect(() => {
        setIsMounted(true);
    }, []);

    const onSaveActionButtonPressed = (action: SaveActionType) => {
        setSaveAction(action);
        if (!isSavable && estimateStatus && fetchedEstimate) {
            notifyFromStatus({ estimateStatus: estimateStatus, fetchedEstimate: fetchedEstimate });
            return;
        }
        trackBeginCheckout({ pricedProducts, priceTotals });
        dispatch(updateIsSavingEstimate(false));
        if (canOverrideSmallJob) {
            setSaveProcessState(SAVE_PROCESS.CONFIRM_SMALL_PROJECT);
        } else {
            setSaveProcessState(SAVE_PROCESS.GET_CUSTOMER_DATA);
        }
    };

    const saveActions = useMemo(() => {
        const retailerActions = retailerSettings?.saveActions ?? emptyArray;
        const fetchActions = fetchedEstimate?.saveActions ?? emptyArray;
        // remove duplicate actions, keeping the first found
        return [...fetchActions, ...retailerActions].filter(
            (action, index, self) => self.findIndex((a) => a.saveAction === action.saveAction) === index
        );
    }, [fetchedEstimate?.saveActions, retailerSettings?.saveActions]);

    const confirmPrintInternal = (password: string | undefined) => {
        if (!password) {
            setShowPasswordModal(false);
            return;
        }
        printPdf({ fetchedEstimate, pdfType: PDFType.INTERNAL, userToken, password }).then((result) => {
            setShowPasswordModal(!result);
            if (result) {
                resetPrintPassword(); // only reset on success so they can try again
            }
        });
    };

    return (
        <div id="Order Summery" className="product-list-container">
            <CustomerExpectationModal
                setAgreementSelected={setAgreementSelected}
                setShowCustomerExpecationModal={setShowCustomerExpecationModal}
                showCustomerExpecationModal={showCustomerExpecationModal}
            />
            <h1 className="summary-header">{t('ORDER_SUMMARY.ORDER_SUMMARY')}</h1>
            <div className="clickable-text" onClick={onClose}>
                {t('ORDER_SUMMARY.RETURN_TO_DESIGN')}
            </div>
            <Divider className="divider-small" />
            <ProductList pricedProducts={pricedProductsWithoutDelivery} />
            <Divider className="divider-small" />
            <ProductSummary
                deliveryCost={matchingMileageTierAsPricedProduct?.price}
                showMinQtyWarning={useMinQtyAreaForPricing}
                promotionEndDate={promotionEndDate}
                totals={priceTotalsWithoutDelivery}
            />
            {useMinQtyAreaForPricing && selectedMaterial ? (
                <div className="price-minimum-warning">
                    {t('PRICING_AREA.MINIMUM_PURCHASE_NOTE', { sqft: smallJobMinimumArea })}
                </div>
            ) : null}
            {canOverrideSmallJob && validatedTicketNumber ? (
                <div className="ticket-message">Small Project Override Code: {validatedTicketNumber}</div>
            ) : null}

            <div className="button-container">
                {showSaveButtons ? (
                    <>
                        <CustomerAgreement />
                        <SaveButton
                            disabled={!isSaveButtonEnabled}
                            onSavePressed={onSaveActionButtonPressed}
                            altButtonText={!isSavable ? 'Save Disabled' : undefined}
                            saveActionValue={DEFAULT_SAVE_ACTION}
                        />
                        {saveActions?.length
                            ? saveActions.map((action) => {
                                  return (
                                      <SaveButton
                                          key={'save_' + action.saveAction}
                                          disabled={!isSaveButtonEnabled}
                                          onSavePressed={onSaveActionButtonPressed}
                                          altButtonText={!isSavable ? 'Save Disabled' : action.description}
                                          saveActionValue={action.saveAction}
                                      />
                                  );
                              })
                            : null}
                    </>
                ) : (
                    <>
                        <PasswordModal
                            password={printPassword}
                            setPassword={setPrintPassword}
                            resetPassword={resetPrintPassword}
                            isVisible={showPasswordModal}
                            onConfirm={(value) => confirmPrintInternal(value)}
                            onCancel={() => {
                                setShowPasswordModal(false);
                            }}
                        />
                        {userOptions?.printInternalDocument ? (
                            <PrintEstimateButton
                                disabled={!isPrintingEnabled}
                                altButtonText={`Print ${fetchedEstimate?.estimateNumber} With Internal Details`}
                                onClick={() => setShowPasswordModal(true)}
                                isInternal
                            />
                        ) : null}

                        <PrintEstimateButton
                            disabled={!isPrintingEnabled}
                            altButtonText={`Print ${fetchedEstimate?.estimateNumber}`}
                            onClick={() => printPdf({ fetchedEstimate, pdfType: PDFType.RETAIL })}
                        />

                        {isPayable ? <PaymentButton paymentUrl={paymentUrl} disabled={!isPayable} /> : null}
                    </>
                )}
            </div>
            <AdditionalInformation />
            <div className="disclaimer">{t('ORDER_SUMMARY.ESTIMATE_SECTION.DISCLAIMER')}</div>
            <CustomerDataModal
                initialCustomerData={customerData}
                isSavingEstimate={isSavingEstimate}
                isFetchedCustomerData={isFetchedCustomerData}
                visible={isSaveModalVisible}
                onCancel={onCustomerDataCancel}
                onSave={onCustomerDataSave}
                showSendEmailOption={retailerFileSettings?.showSendEmailOption ?? !!retailerSettings?.isOnlineRetailer} // showSendEmailOption only required to override default
            />
            <ConfirmSmallProject
                value={validatedTicketNumber}
                isVisible={isSmallProjectModalVisible}
                minSqFt={smallJobMinimumArea}
                onContinue={onSmallProjectContinue}
                onSuccess={onSmallProjectSuccess}
                onCancel={onSmallProjectCancel}
            />
        </div>
    );
};

export default EstimateSection;
